/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import { useNavigate } from 'react-router-dom';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import { useJobRunTypes } from '@Hooks/useJobRuns';
import type { JobStatus } from '@Types/jobRun';

const STATUS_VARIANTS: Record<JobStatus, 'default' | 'destructive' | 'secondary'> = {
  SUCCESS: 'default',
  FAILURE: 'destructive',
  PENDING: 'secondary',
};

export default function JobRuns() {
  const navigate = useNavigate();
  const { data: response, status } = useJobRunTypes();
  const types = response?.data ?? [];

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/job-runs' },
          { name: 'Job Runs', href: '/job-runs' },
        ]}
      />
      <div className="mt-8 mb-4">
        <Heading>Job Runs</Heading>
        <p className="text-sm text-zinc-500">View cron job execution history by type.</p>
      </div>
      <Card>
        <CardContent>
          {status === 'pending' ? (
            <Loader size="lg" />
          ) : !types.length ? (
            <div className="py-8 text-center text-sm text-zinc-500">
              No job runs recorded yet.
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Total Runs</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead>Last Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((t) => (
                  <TableRow
                    key={t.type}
                    className="cursor-pointer"
                    onClick={() => navigate(`/job-runs/type/${encodeURIComponent(t.type)}`)}
                  >
                    <TableCell className="font-mono text-sm font-medium">
                      {t.type}
                    </TableCell>
                    <TableCell>{t.runCount}</TableCell>
                    <TableCell>
                      {t.lastRunAt ? new Date(t.lastRunAt).toLocaleString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {t.lastStatus ? (
                        <Badge variant={STATUS_VARIANTS[t.lastStatus]}>
                          {t.lastStatus}
                        </Badge>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
